import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ComponentRenderer from '../components/ComponentRenderer';
import SEO from '../components/SEO';
import SafeMarkdown from '../components/SafeMarkdown';
import WidgetRenderer from '../components/WidgetRenderer';
import { useAuth } from '../context/AuthContext';
import { buildProblemSourceDraft, problemDraftStorageKey } from '../data/problems';
import { profileThemeClass } from '../data/profileThemes';
import { listVisibleUserComponents } from '../services/componentService';
import { listVisibleComponentRuns } from '../services/componentRunService';
import { listVisiblePageEntries, listVisibleStories, listVisibleTimelineEvents } from '../services/contentService';
import { isAcceptedFriend } from '../services/friendService';
import { getPublicProfileByUsername } from '../services/profileService';
import { listVisibleUserWidgets } from '../services/widgetService';

function PublicProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stories, setStories] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [pageEntries, setPageEntries] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [components, setComponents] = useState([]);
  const [componentRuns, setComponentRuns] = useState([]);
  const [viewerIsFriend, setViewerIsFriend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function loadProfile() {
      setLoading(true);
      setError('');
      try {
        const loadedProfile = await getPublicProfileByUsername(username);
        if (active) {
          setProfile(loadedProfile);
        }
        if (loadedProfile) {
          const viewerUid = currentUser?.uid || null;
          const friendVisible = await isAcceptedFriend(loadedProfile.ownerId, viewerUid);
          const [loadedStories, loadedTimelineEvents, loadedPages, loadedWidgets, loadedComponents, loadedRuns] = await Promise.all([
            listVisibleStories(loadedProfile.ownerId, viewerUid),
            listVisibleTimelineEvents(loadedProfile.ownerId, viewerUid),
            listVisiblePageEntries(loadedProfile.ownerId, viewerUid),
            listVisibleUserWidgets(loadedProfile.ownerId, viewerUid),
            listVisibleUserComponents(loadedProfile.ownerId, viewerUid),
            listVisibleComponentRuns(loadedProfile.ownerId, viewerUid),
          ]);
          if (active) {
            setViewerIsFriend(friendVisible);
            setStories(loadedStories);
            setTimelineEvents(loadedTimelineEvents);
            setPageEntries(loadedPages || []);
            setWidgets(loadedWidgets);
            setComponents(loadedComponents);
            setComponentRuns(loadedRuns);
          }
        }
      } catch (err) {
        if (active) {
          setError(err.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      active = false;
    };
  }, [currentUser, username]);

  if (loading) {
    return <div className="surface-card">Loading public profile...</div>;
  }

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  if (!profile) {
    return (
      <section className="auth-panel">
        <SEO title="Profile Not Found" />
        <h1>Profile not found</h1>
        <p>This profile is private, unpublished, or does not exist.</p>
      </section>
    );
  }

  const publicSections = profile.sections.filter((section) => (
    section.visibility === 'public'
    || currentUser?.uid === profile.ownerId
    || (section.visibility === 'friends' && viewerIsFriend)
  ));

  const profileComponents = components.filter((component) => (
    component.placement === 'profile' || component.placement === 'both'
  ));
  const profilePageEntries = pageEntries.filter((entry) => entry.showOnProfile);

  function renderSectionContent(section) {
    if (section.id === 'hero') {
      return <SafeMarkdown>{profile.bio}</SafeMarkdown>;
    }

    if (section.id === 'stories') {
      return stories.length ? (
        <div className="embedded-list">
          {stories.slice(0, 4).map((story) => (
            <article key={story.id}>
              <h3>
                <Link to={`/u/${username}/stories/${story.id}`}>{story.title}</Link>
              </h3>
              <SafeMarkdown>{story.summary || story.content}</SafeMarkdown>
            </article>
          ))}
        </div>
      ) : <p>No public stories yet.</p>;
    }

    if (section.id === 'timeline') {
      return timelineEvents.length ? (
        <div className="embedded-list timeline-mini">
          {timelineEvents.slice(0, 5).map((event) => (
            <article key={event.id}>
              <span>{event.dateLabel || event.sortYear || 'Undated'}</span>
              <h3>{event.title}</h3>
              <p>{event.description}</p>
            </article>
          ))}
        </div>
      ) : <p>No public timeline events yet.</p>;
    }

    if (section.id === 'widgets' || section.type === 'widgets') {
      return widgets.length ? (
        <div className="embedded-list widget-section-list">
          {widgets.map((widget) => (
            <article className="widget-profile-section" key={widget.id}>
              <WidgetRenderer widget={widget} />
            </article>
          ))}
        </div>
      ) : <p>No public widgets yet.</p>;
    }

    if (section.type === 'custom') {
      return <SafeMarkdown>{section.content || 'This custom section is still being written.'}</SafeMarkdown>;
    }

    return <p>Public content for this section will render here.</p>;
  }

  function createProblemFromEntry(entry) {
    localStorage.setItem(problemDraftStorageKey, JSON.stringify(buildProblemSourceDraft({
      sourceType: 'pageEntry',
      sourceId: entry.id,
      sourceTitle: entry.prompt,
      sourceText: entry.answer,
    })));
    navigate('/dashboard/problems');
  }

  return (
    <>
      <SEO title={`${username} Profile`} description="A public Cyberautography profile preview." />
      <section
        className={`profile-preview ${profileThemeClass(profile.theme?.template)}`}
        style={{ '--profile-accent': profile.theme?.accentColor || '#8a5a34' }}
      >
        <div className="profile-hero">
          <p className="eyebrow">Public autobiography</p>
          <h1>{profile.displayName}</h1>
          <p>{profile.tagline}</p>
        </div>
        <div className="profile-grid">
          {profileComponents.length > 0 ? profileComponents.map((component) => (
            <article className={`profile-section profile-section-${component.layoutWidth || 'half'}`} key={component.id}>
              <span className="page-label">{component.kind}</span>
              <h2>{component.title}</h2>
              <ComponentRenderer
                component={component}
                runs={componentRuns}
                stories={stories}
                timelineEvents={timelineEvents}
                widgets={widgets}
                username={username}
              />
            </article>
          )) : publicSections.map((section) => (
            <article className="profile-section" key={section.id}>
              <span className="page-label">{section.type}</span>
              <h2>{section.title}</h2>
              {renderSectionContent(section)}
            </article>
          ))}
          {profilePageEntries.length > 0 ? (
            <article className="profile-section profile-section-full">
              <span className="page-label">pages</span>
              <h2>Pages</h2>
              <div className="page-tile-grid compact-page-grid">
                {profilePageEntries.slice(0, 6).map((entry) => (
                  <div className={`page-tile page-tile-${entry.tileFormat || 'square'}`} key={entry.id}>
                    <div className="tile-action-row">
                      <p className="tag">{entry.theme} · {entry.entryDate}</p>
                      <button className="tile-arrow-button" type="button" title="Open a problem from this tile" onClick={() => createProblemFromEntry(entry)}>-&gt;</button>
                    </div>
                    <h3>{entry.prompt}</h3>
                    {entry.imageUrl ? <img src={entry.imageUrl} alt="Page attachment" /> : null}
                    <p>{entry.answer}</p>
                  </div>
                ))}
              </div>
              <Link className="button button-secondary" to={`/u/${username}/pages`}>View all pages</Link>
            </article>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default PublicProfile;
